import React from 'react';
import { FaCreditCard, FaCheckCircle, FaHourglassHalf, FaInfoCircle } from 'react-icons/fa';

// Ayushpay Credit activation status — mirrors WalletActivationCard.jsx's
// header + status-banner visual pattern, for the credit-line lending
// product (a different concept from wallet PIN activation).
const CreditActivationCard = ({ isOpsOrSuperAdmin, creditSummary, loading }) => {
  if (!isOpsOrSuperAdmin) return null;

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden animate-pulse">
        <div className="px-6 py-5 border-b border-slate-100 h-20" />
        <div className="px-6 py-5 h-20" />
      </div>
    );
  }

  if (!creditSummary?.has_credit_line_account) return null;

  const activationStatus = creditSummary.activation_status;

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300">
      {/* Header */}
      <div className="relative px-6 py-5 bg-gradient-to-r from-slate-50 via-white to-white border-b border-slate-100 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#0F4C81] to-[#00A6A6] flex items-center justify-center shadow-md shadow-[#0F4C81]/20">
          <FaCreditCard className="w-4 h-4 text-white" />
        </div>
        <div>
          <h2 className="text-base font-bold text-slate-900 tracking-tight">Ayushpay Credit</h2>
          <p className="text-xs text-slate-500 mt-0.5">Credit line activation status</p>
        </div>
      </div>

      {/* Status banner */}
      <div className="px-6 py-5">
        {activationStatus === 'active' ? (
          <div className="relative overflow-hidden bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 border border-green-200 rounded-2xl px-5 py-4 shadow-sm">
            <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-green-300/30 blur-3xl" />
            <div className="relative flex items-center gap-4">
              <div className="relative">
                <div className="absolute inset-0 rounded-2xl bg-green-400 blur-md opacity-40 animate-pulse" />
                <div className="relative w-12 h-12 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-green-500/40">
                  <FaCheckCircle className="w-5 h-5 text-white" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-bold text-green-900">Ayushpay Credit Activated</p>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500 text-white text-[10px] font-bold uppercase tracking-wide shadow-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                    Live
                  </span>
                </div>
                <p className="text-xs text-green-700 mt-1">Credit line account and lender are both active</p>
              </div>
            </div>
          </div>
        ) : activationStatus === 'pre_approved' ? (
          <div className="relative overflow-hidden bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 border border-amber-200 rounded-2xl px-5 py-4 shadow-sm">
            <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-amber-300/30 blur-3xl" />
            <div className="relative flex items-center gap-4">
              <div className="relative">
                <div className="absolute inset-0 rounded-2xl bg-amber-400 blur-md opacity-40 animate-pulse" />
                <div className="relative w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/40">
                  <FaHourglassHalf className="w-5 h-5 text-white animate-pulse" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-bold text-amber-900">Ayushpay Credit Activation</p>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wide shadow-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                    Pre-approved
                  </span>
                </div>
                <p className="text-xs text-amber-700 mt-1">Credit line account is pre-approved, not yet active</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-slate-50 border border-slate-200 rounded-2xl px-5 py-4">
            <div className="relative flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm border border-slate-200">
                <FaInfoCircle className="w-5 h-5 text-slate-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-slate-700">No Active Credit Status</p>
                <p className="text-xs text-slate-500 mt-1">Credit line account exists but is not pre-approved or active</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreditActivationCard;
